/**
 * Hook: GET /api/t/{slug}/pipeline-stages — tenant-configured pipeline stages.
 *
 * Phase 2.2 — Dashboard / Per-JD Pipeline View. Returns the ordered stage
 * list for the tenant; consumed by `<StageProgressionBar>` to render the
 * top-of-page progression dots + per-stage labels.
 *
 * Cache key: `["pipeline-stages", tenantSlug]` (Architect §3).
 *
 * Response shape mirrors `app/schemas/pipeline_stages.py::PipelineStagesListResponse`:
 *   `{ items: PipelineStageOut[] }`.
 *
 * Stages include both progression (is_terminal=false) and terminal
 * (is_terminal=true) rows; consumers filter by `is_terminal` per spec
 * §3.2 active-furthest semantics (terminals excluded from the bar's
 * step-numbering).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";

interface PipelineStage {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  order_index: number;
  is_terminal: boolean;
  terminal_outcome: "hired" | "rejected" | "opted_out" | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

interface PipelineStagesListResponse {
  items: PipelineStage[];
}

export function useTenantPipelineStages(): UseQueryResult<
  PipelineStage[],
  ApiError
> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return useQuery<PipelineStage[], ApiError>({
    queryKey: ["pipeline-stages", tenantSlug],
    queryFn: async () => {
      const r = await apiClient.get<PipelineStagesListResponse>(
        `/api/t/${tenantSlug}/pipeline-stages`,
      );
      return r.data.items;
    },
    enabled: !!tenantSlug,
    staleTime: 60_000,
  });
}
